/**
 * Danske autocamperpladser — anviste bobilplasser høstet fra OSM.
 *
 * HVORFOR DENNE FILEN FINNES:
 *
 * `scripts/prescan-denmark.mjs` skriver disse plassene med
 *
 *   spotType: 'algorithmic',
 *   sourceLevel: 4,
 *   features: ['AUTOCAMPER', 'DENMARK', 'DESIGNATED', 'VEHICLE_OK']
 *
 * `sourceLevel: 4` er der som VERN: alle opprydningsskriptene lar
 * `sourceLevel >= 4` stå urørt. Men UI-et leste det samme tallet som
 * «brukerbidrag», så nålene ble rosa `fa-user-check` (MapUtils) — 79 av dem over
 * Jylland, målt i prod 23.08.2026 — og `spotType: 'algorithmic'` la dem på
 * fricamp-laget i stedet for bobilplass-laget.
 *
 * `AUTOCAMPER` var det eneste feltet som identifiserte dem, og fram til nå fantes
 * strengen KUN i seed-scriptet: ingenting i appen visste hva plassene var. Derfor
 * bor testen her, ett sted, brukt av markørikonet (`MapUtils`) og av de to
 * lagfiltrene (`components/Map.tsx` for kartet, `App.tsx` for listen).
 *
 * MERK at plassene ikke er `spotType: 'commercial_campsite'`. Å endre det ville
 * krevd backfill av alle danske autocamperpladser i prod, og `commercial_campsite`
 * bærer mer med seg enn laget (kontaktinfo-blokk, stjerne-i-stedet-for-score,
 * unntak fra 150 m-regelen). Feature-flagget gjør jobben uten datamigrering.
 */

/** Feltet seed-scriptet setter. Sammenlignes i UPPERCASE, som ellers i kodebasen. */
const AUTOCAMPER_FEATURE = 'AUTOCAMPER';

/**
 * True for en anvist autocamperplads. Tåler `features: undefined` og
 * ikke-streng-elementer, slik resten av feature-testene gjør.
 */
export function isAutocamperSpot(
  spot: { features?: unknown } | null | undefined,
): boolean {
  const features = spot && Array.isArray(spot.features) ? spot.features : null;
  if (!features) return false;
  return features.some(f => String(f).toUpperCase() === AUTOCAMPER_FEATURE);
}

/**
 * Er plassen laget av en BRUKER?
 *
 * `sourceLevel === 4` alene svarer ikke på det: autocamperpladsene bærer samme
 * nivå som vern (se filhodet). Denne testen er derfor sannheten for alt som
 * skal vises som brukerbidrag — rosa nål og bruker-badge (`MapUtils`),
 * «BRUKER»-kilde-chip, bruker-avatar og «lagt til av»-linjen (`SpotCard`,
 * `MobileSpotSheet`).
 */
export function isUserCreatedSpot(
  spot: { sourceLevel?: number; features?: unknown } | null | undefined,
): boolean {
  return !!spot && spot.sourceLevel === 4 && !isAutocamperSpot(spot);
}

/**
 * Er plassen en ANVIST bobil-/campingplass — altså en plass noen driver, ikke en
 * villmarksplass vi har funnet?
 *
 * Styrer kort-oppsettet: stjernevurdering i stedet for fricamp-score,
 * kontaktinfo-blokk, ingen allemannsretts-analyse, bobilplass-ikon. Kommersielle
 * campingplasser og bobilplasser (`commercial_campsite`) har alltid vært dette;
 * autocamperpladsene ble lest som villmark fordi de er lagret som
 * `spotType: 'algorithmic'`.
 */
export function isManagedCampingSpot(
  spot: { spotType?: string; features?: unknown } | null | undefined,
): boolean {
  if (!spot) return false;
  return spot.spotType === 'commercial_campsite' || isAutocamperSpot(spot);
}
